import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import MainContainer from '../../components/MainContainer/MainContainer';
import Accordion, { SCROLL_OFFSET_NAV_FIXED } from '../../components/Accordion/Accordion';
import { obterCarteiraDocumentosFamilia } from '../../api/familiaAPI';
import { excluirDocumento } from '../../api/carteiraDocumentosAPI';
import { selectPerfilCompleto, selectSituacaoCadastroFamilia } from '../Perfil/perfilSlice';
import { obterNomeDoPerfil } from '../../common/perfilUtils';
import { trackServiceSelect } from '../../common/tracker';
import { authLogout, selectGateway } from '../../auth/authSlice';
import auth from '../../auth/Auth';
import LoadingGlobal from '../../components/LoadingGlobal/LoadingGlobal';
import styles from './CarteiraDocumentos.module.css';
import AjudaRapida from '../../components/AjudaRapida/AjudaRapida';
import DocumentoItem from './DocumentoItem';
import SelecionarDocumento from './SelecionarDocumento/SelecionarDocumento';
import UploadDocumento from './UploadDocumento/UploadDocumento';
import PreviewImagem from './Components/PreviewImagem';
import VisualizarDocumento from './VisualizarDocumento/VisualizarDocumento';
import {
  CarteiraDocumentosState,
  selectCarteiraDocumentosState,
  irParaSelecionarDocumento,
  irParaVisualizarDocumento,
  voltarParaLista,
  voltarParaSelecionar,
  voltarParaUpload,
  limparSucesso,
  limparErro,
} from './carteiraDocumentosSlice';

interface DocumentoAPI {
  id: number;
  tipoDocumento: {
    codigo: number;
    descricao: string;
  };
  nomeArquivo: string;
  formato: string;
  tamanhoKB: number;
  referenciaDocumento: string;
}

interface MembroAPI {
  idIntegrante: number;
  nomePessoa: string;
  cpfIntegrante: number;
  estadoCadastral: {
    codigo: number;
    nome: string;
  };
  tipoRelacaoParentescoRF?: {
    codigo: number;
    nome: string;
  };
  documentos: DocumentoAPI[];
}

const MENSAGENS_ESTADO_CADASTRAL: Record<number, string> = {
  1: 'Carteira de Documentos não disponível. Pessoa em cadastramento. O Responsável Familiar deve procurar o posto de atendimento do Cadastro Único.',
  2: 'Carteira de Documentos não disponível. Pessoa sem registro civil. O Responsável Familiar deve procurar o posto de atendimento do Cadastro Único.',
  7: 'Carteira de Documentos não disponível. Pessoa aguardando CPF. O Responsável Familiar deve procurar o posto de atendimento do Cadastro Único.',
};

const MAPA_DOCUMENTOS: Record<number, { titulo: string; subtitulo: string }> = {
  1: { titulo: 'CIN', subtitulo: 'Carteira de Identidade Nacional' },
  2: { titulo: 'CNH', subtitulo: 'Carteira Nacional de Habilitação' },
  3: { titulo: 'e-Título', subtitulo: 'Título de Eleitor Digital' },
};

const CarteiraDocumentos: React.FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [documentoParaExcluir, setDocumentoParaExcluir] = useState<{integranteId: string, tipoDoc: string, id: number} | null>(null);
  
  const [integrantes, setIntegrantes] = useState<MembroAPI[]>([]);
  const [loading, setLoading] = useState(false);

  const perfil = useAppSelector(selectPerfilCompleto);
  const situacaoCadastroFamilia = useAppSelector(selectSituacaoCadastroFamilia);
  const gatewayLogado = useAppSelector(selectGateway).logado;
  const state = useAppSelector(selectCarteiraDocumentosState);
  const documentoIncluidoComSucesso = useAppSelector(s => s.carteiraDocumentos.documentoIncluidoComSucesso);
  const documentoExcluidoComSucesso = useAppSelector(s => s.carteiraDocumentos.documentoExcluidoComSucesso);
  const erroInclusao = useAppSelector(s => s.carteiraDocumentos.erroInclusao);

  const carregarIntegrantes = (numeroFamiliar: number) => {
    setLoading(true);
    obterCarteiraDocumentosFamilia(numeroFamiliar)
      .then((data) => {
        console.log('[CarteiraDocumentos] retorno API:', data);
        setIntegrantes(data?.integrantes ?? []);
      })
      .catch((err) => console.error('[CarteiraDocumentos] erro API:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (perfil?.numeroFamiliar) carregarIntegrantes(perfil.numeroFamiliar);
  }, [perfil?.numeroFamiliar]);

  useEffect(() => {
    if (documentoIncluidoComSucesso && perfil?.numeroFamiliar) {
      carregarIntegrantes(perfil.numeroFamiliar);
    }
  }, [documentoIncluidoComSucesso]);

  useEffect(() => {
    if (documentoExcluidoComSucesso && perfil?.numeroFamiliar) {
      carregarIntegrantes(perfil.numeroFamiliar);
    }
  }, [documentoExcluidoComSucesso]);

  useEffect(() => {
    const rootEl = document.getElementById('root');
    rootEl?.scrollTo(0, 0);
  }, [state]);

  const getTitulo = () => {
    switch (state) {
      case CarteiraDocumentosState.SELECIONAR_DOCUMENTO:
        return 'Carteira de documentos';
      case CarteiraDocumentosState.UPLOAD_DOCUMENTO:
        return 'Documentos';
      case CarteiraDocumentosState.PREVIEW_IMAGEM:
        return 'Documentos';
      case CarteiraDocumentosState.VISUALIZAR_DOCUMENTO:
        return 'Carteira de documentos';
      default:
        return 'Carteira de documentos';
    }
  };

  const caixaMensagens = () => {
    trackServiceSelect('POR-MENU', "CAIXA DE MENSAGEM");
    history.push('/caixa-mensagem');
  };

  const logout = () => {
    dispatch(authLogout({ history }));
  };

  const isLogado = () => {
    return !!(auth.isAuthenticated() && auth.getUser() && gatewayLogado);
  };

  const goBack = () => {
    switch (state) {
      case CarteiraDocumentosState.SELECIONAR_DOCUMENTO:
        return dispatch(voltarParaLista());
      case CarteiraDocumentosState.UPLOAD_DOCUMENTO:
        return dispatch(voltarParaSelecionar());
      case CarteiraDocumentosState.PREVIEW_IMAGEM:
        return dispatch(voltarParaUpload());
      case CarteiraDocumentosState.VISUALIZAR_DOCUMENTO:
        return dispatch(voltarParaLista());
      default:
        history.goBack();
    }
  };

  const handleDocumentoClick = (integranteId: string, tipoDoc: string, referenciaDocumento: string) => {
    const membro = integrantes.find(m => String(m.idIntegrante) === integranteId);
    if (!membro) return;

    const codigo = Number(tipoDoc);
    const info = MAPA_DOCUMENTOS[codigo];
    const nomeDocumento = info ? `${info.titulo} - ${info.subtitulo}` : tipoDoc;
    const doc = membro.documentos.find(d => String(d.tipoDocumento.codigo) === tipoDoc);

    dispatch(irParaVisualizarDocumento({
      integranteNome: membro.nomePessoa,
      tipoDocumento: tipoDoc,
      nomeDocumento,
      dataEnvio: '',
      referenciaDocumento,
      idDocumento: doc?.id ?? 0,
    }));
  };

  const handleExcluirClick = (integranteId: string, tipoDoc: string, id: number) => {
    setDocumentoParaExcluir({ integranteId, tipoDoc, id });
    setShowModal(true);
  };

  const confirmarExclusao = async () => {
    if (documentoParaExcluir) {
      try {
        await excluirDocumento(documentoParaExcluir.id);
        setIntegrantes(prev => prev.map(m => ({
          ...m,
          documentos: m.documentos.filter(d => d.id !== documentoParaExcluir.id),
        })));
      } catch (err: any) {
        alert(err?.message || 'Erro ao excluir o documento. Tente novamente.');
      }
    }
    setShowModal(false);
    setDocumentoParaExcluir(null);
  };

  const cancelarExclusao = () => {
    setShowModal(false);
    setDocumentoParaExcluir(null);
  };

  const handleAdicionarDocumento = (integranteId: string) => {
    const membro = integrantes.find(m => String(m.idIntegrante) === integranteId);
    if (!membro) return;
    dispatch(irParaSelecionarDocumento({
      integranteId: String(membro.idIntegrante),
      integranteNome: membro.nomePessoa,
      cpfIntegrante: String(membro.cpfIntegrante),
      tiposDocumentoExistentes: membro.documentos.map(d => String(d.tipoDocumento.codigo)),
    }));
  };

  const TOTAL_TIPOS_DOCUMENTO = Object.keys(MAPA_DOCUMENTOS).length;

  const renderDocumentos = (membro: MembroAPI) => {
    const mensagemBloqueio = MENSAGENS_ESTADO_CADASTRAL[membro.estadoCadastral?.codigo];

    return (
      <div className={styles.documentosContainer}>
        {membro.documentos.map((doc) => {
          const info = MAPA_DOCUMENTOS[doc.tipoDocumento.codigo];
          return (
            <DocumentoItem
              key={doc.tipoDocumento.codigo}
              documento={{
                key: String(doc.tipoDocumento.codigo),
                titulo: info?.titulo ?? doc.tipoDocumento.descricao,
                subtitulo: info?.subtitulo ?? '',
                referenciaDocumento: doc.referenciaDocumento,
                id: doc.id,
              }}
              integranteId={String(membro.idIntegrante)}
              onVisualizarClick={handleDocumentoClick}
              onExcluirClick={handleExcluirClick}
            />
          );
        })}

        {mensagemBloqueio ? (
          <div className={styles.mensagemBloqueio}>
            <p>{mensagemBloqueio}</p>
          </div>
        ) : (
          membro.documentos.length < TOTAL_TIPOS_DOCUMENTO && (
            <div className={styles.adicionarDocumentoContainer}>
              <button
                className={styles.adicionarDocumentoButton}
                onClick={() => handleAdicionarDocumento(String(membro.idIntegrante))}
              >
                <i className="fas fa-plus"></i>
                Adicionar documento
              </button>
            </div>
          )
        )}
      </div>
    );
  };

  if (loading) {
    return <LoadingGlobal />;
  }

  const telaLista = (
    <>
      {documentoIncluidoComSucesso && (
        <div className={styles.alertaSucesso}>
          <i className="fas fa-check-circle"></i>
          Documento incluído com sucesso!
          <button className={styles.alertaFechar} onClick={() => dispatch(limparSucesso())}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      <div className={styles.container}>
        {integrantes.map((membro, index) => (
          <Accordion
            key={membro.idIntegrante}
            id={`integrante-${membro.idIntegrante}`}
            title={membro.nomePessoa}
            subtitle={membro.tipoRelacaoParentescoRF?.nome ?? ''}
            open={index === 0}
            scroll
            scrollOffset={SCROLL_OFFSET_NAV_FIXED}
            className={styles.integranteAccordion}
          >
            {renderDocumentos(membro)}
          </Accordion>
        ))}
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={cancelarExclusao}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p className={styles.modalTexto}>
              Tem certeza que deseja excluir este documento?
            </p>
            <div className={styles.modalBotoes}>
              <button className={styles.botaoConfirmar} onClick={confirmarExclusao}>
                Sim, quero excluir
              </button>
              <button className={styles.botaoCancelar} onClick={cancelarExclusao}>
                Não
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.ajudaRapidaContainer}>
        <AjudaRapida>
          O Responsável Familiar (RF) pode guardar <b>documentos biométricos digitais</b> na <b>carteira de documentos</b> dos integrantes da sua família. 
          <p>
            <br/>
            São aceitos:
            <br/>&nbsp;- CIN (Carteira de Identidade Nacional);
            <br/>&nbsp;- CNH (Carteira Nacional de Habilitação);
            <br/>&nbsp;- e-Título (Título de Eleitor Digital).
          </p>
          <p>
            Assim, não será mais necessária a apresentação destes documentos no atendimento presencial.
          </p>
        </AjudaRapida>
      </div>
    </>
  );

  return (
    <MainContainer
      navTitle={obterNomeDoPerfil(perfil, situacaoCadastroFamilia)}
      titleBarraAuxiliar={getTitulo()}
      navSeparator
      userNav={isLogado()}
      barraAuxiliar={true}
      stoppedTitle={true}
      navFixed={isLogado()}
      navGoBack={goBack}
      navHideArrow={false}
      navCaixaMensagens={isLogado() ? caixaMensagens : undefined}
      navLogout={isLogado() ? logout : undefined}
    >
      {state === CarteiraDocumentosState.LISTA && telaLista}
      {state === CarteiraDocumentosState.SELECIONAR_DOCUMENTO && (
        <>
          {erroInclusao && (
            <div className={styles.alertaErro}>
              <i className="fas fa-times-circle"></i>
              {erroInclusao}
            </div>
          )}
          <SelecionarDocumento />
        </>
      )}
      {state === CarteiraDocumentosState.UPLOAD_DOCUMENTO && <UploadDocumento />}
      {state === CarteiraDocumentosState.PREVIEW_IMAGEM && <PreviewImagem />}
      {state === CarteiraDocumentosState.VISUALIZAR_DOCUMENTO && <VisualizarDocumento />}
    </MainContainer>
  );
};

export default CarteiraDocumentos;
